import {PaymentDetails, PaymentType} from "@bounce/shared/consumer/declarations";
import {AddPaymentMethodDialogContent} from "@bounce/shared/consumer/pages/account-dashboard";
import NiceModal, {NiceModalHocProps, useModal} from "@ebay/nice-modal-react";
import React, {useCallback, useEffect, useState} from "react";
import {useAddPaymentMethod, useGetDashboardQuery} from "~/api/requests";
import {Pages} from "~/bi-events/enum";
import {toast} from "sonner";
import {useAmplitude} from "~/contexts";
import PaymentMethod from "./PaymentMethod";

export type IAddPaymentMethod = {
  accountId: string;
  title: string;
  onPaymentSuccess?: () => void;
  onPaymentFailed?: () => void;
  activePaymentPlanId: string;
  inManagePaymentMethod?: boolean;
  onlyMethodChange?: boolean;
};

const AddPaymentMethodDialog = NiceModal.create(
  ({
    accountId,
    title,
    onPaymentSuccess,
    onPaymentFailed,
    activePaymentPlanId,
    inManagePaymentMethod = false,
    onlyMethodChange = false,
  }: IAddPaymentMethod & NiceModalHocProps) => {
    const {pageViewEvent, pageClickEvent} = useAmplitude();
    const modal = useModal();
    const [buttonTouched, setButtonTouched] = useState(false);

    const userDashboard = useGetDashboardQuery(
      {
        account_id: accountId,
        payment_plan_id: activePaymentPlanId,
      },
      {enabled: !!activePaymentPlanId}
    );

    const addPaymentMethod = useAddPaymentMethod({refetchType: "active"});

    const addPaymentMethodHandler = useCallback(
      async (paymentType: PaymentType, paymentDetails: PaymentDetails): Promise<void> => {
        let error;
        let applePayResult = false;
        try {
          const result = await addPaymentMethod.mutateAsync({
            ...paymentDetails,
            account_id: accountId,
            payment_plan_id: activePaymentPlanId,
            only_method_change: onlyMethodChange,
          });
          if (result.success) {
            paymentSuccessHandler(paymentDetails);
            applePayResult = true;
          } else {
            toast.error(`Failed to update payment method - ${result.error_message}`);
            setButtonTouched(false);
            applePayResult = false;
          }
        } catch (e) {
          paymentFailedHandler();
          applePayResult = false;
        } finally {
          if (paymentType === "ApplePay" && !!paymentDetails.applePayCompletionFunction) {
            paymentDetails.applePayCompletionFunction(applePayResult);
          }
          pageClickEvent({
            page: Pages.MANAGE_PAYMENT_METHOD,
            cta: "save_changes",
            data: {consent: true, payment_method: paymentType, error, is_prepaid: paymentDetails.is_prepaid},
          });
        }
      },
      [accountId, addPaymentMethod, userDashboard.data]
    );

    const paymentSuccessHandler = useCallback(
      (paymentDetails: PaymentDetails): void => {
        const typeName = paymentDetails.ach ? "ACH" : "Card";
        onPaymentSuccess?.();
        toast.success(`${typeName} payment method was added successfully.`);
        modal.resolve(true);
        modal.hide();
      },
      [onPaymentSuccess]
    );

    const paymentFailedHandler = useCallback((): void => {
      setButtonTouched(false);
      onPaymentFailed?.();
    }, [onPaymentFailed]);

    useEffect(() => {
      pageViewEvent({page: Pages.MANAGE_PAYMENT_METHOD});
    }, []);

    return (
      <AddPaymentMethodDialogContent
        accountId={accountId}
        paymentHandler={addPaymentMethodHandler}
        title={title}
        submitTitle={inManagePaymentMethod ? "Add" : "Save changes"}
        currentMethodType={userDashboard.data?.payment_method_type}
        page={Pages.MANAGE_PAYMENT_METHOD}
        paymentPlanId={activePaymentPlanId}
        immediatePaymentAmount={"0.00"}
        inManagePaymentMethod={inManagePaymentMethod}
        buttonTouched={buttonTouched}
        setButtonTouched={setButtonTouched}
        isLoading={addPaymentMethod.isLoading}
        PaymentMethod={PaymentMethod}
      />
    );
  }
);

export default AddPaymentMethodDialog;
